import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
export const siteOperationStorageKey = (siteId) => `site-operation:${siteId}`;
const definitive = (failure) => [400, 401, 403, 404, 409].includes(failure.response?.status || failure.status);
export default function useSiteOperation(siteId, { api = axios, executeBasePath, operationBasePath, storagePrefix = '' } = {}) {
  const [operation, setOperation] = useState(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const reading = useRef(null);
  const timer = useRef(null);
  const executeBase = executeBasePath || `/api/sites/${siteId}/power-routines`;
  const operationBase = operationBasePath || `/api/sites/${siteId}/power-operations`;
  const storageKey = storagePrefix + siteOperationStorageKey(siteId);
  const persist = useCallback((value) => {
    setPending(value);
    try {
      if (value) sessionStorage.setItem(storageKey, JSON.stringify(value));
      else sessionStorage.removeItem(storageKey);
    } catch { /* Session storage can be unavailable. */ }
  }, [storageKey]);
  const poll = useCallback(async function read(id, version) {
    if (reading.current === version) return;
    reading.current = version;
    clearTimeout(timer.current);
    try {
      const response = await api.get(`${operationBase}/${id}`, { headers: headers(), timeout: 10000 });
      if (generation.current !== version) return;
      setOperation(response.data); setError('');
      if (response.data.status === 'running' && !response.data.stale) timer.current = setTimeout(() => read(id, version), 1500);
    } catch (failure) {
      if (generation.current !== version) return;
      if (definitive(failure)) { persist(null); setOperation(null); }
      setError(`Could not read site progress: ${failure.response?.data?.message || failure.message}`);
    } finally { if (reading.current === version) reading.current = null; }
  }, [api, operationBase, persist]);
  useEffect(() => {
    const version = ++generation.current;
    inFlight.current = false; reading.current = null;
    setOperation(null); setPending(null); setError(''); setSending(false);
    if (siteId) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (saved && ['startup', 'shutdown'].includes(saved.phase) && typeof saved.key === 'string') {
          setPending(saved);
          if (saved.id) poll(saved.id, version);
          else setError('A previous site request has no response yet. Check the existing request before running another.');
        }
      } catch { /* Ignore invalid or unavailable storage. */ }
    }
    return () => { generation.current += 1; clearTimeout(timer.current); };
  }, [siteId, storageKey, poll]);
  const submit = useCallback(async (phase, recover = false) => {
    if (!siteId || inFlight.current || !['startup', 'shutdown'].includes(phase)) return;
    if (!recover && (operation?.status === 'running' || (pending && !operation))) return;
    inFlight.current = true;
    const version = ++generation.current;
    reading.current = null; clearTimeout(timer.current);
    const request = recover && pending ? pending : { phase,
      key: globalThis.crypto?.randomUUID?.() || `site-${Date.now()}-${Math.random().toString(36).slice(2)}` };
    persist(request); setOperation(null); setSending(true); setError('');
    try {
      const response = await api.post(`${executeBase}/${request.phase}/execute`, { idempotencyKey: request.key },
        { headers: headers(), timeout: 15000 });
      if (generation.current !== version) return;
      persist({ ...request, id: response.data.id }); setOperation(response.data);
      if (response.data.status === 'running' && !response.data.stale) poll(response.data.id, version);
      return response.data;
    } catch (failure) {
      if (generation.current !== version) return;
      if (definitive(failure)) persist(null);
      setError(`${failure.response?.data?.message || failure.message}${definitive(failure) ? '' : '. Check the existing request to recover its result.'}`);
    } finally { if (generation.current === version) { inFlight.current = false; setSending(false); } }
  }, [api, executeBase, siteId, pending, operation, persist, poll]);
  return { operation, phase: operation?.phase || pending?.phase, error, busy: sending || operation?.status === 'running' || Boolean(pending && !operation),
    run: (phase) => submit(phase),
    check: () => pending?.id && !operation?.stale ? poll(pending.id, generation.current) : submit(pending?.phase, true),
    canCheck: Boolean(pending && (!operation || operation.status === 'running')),
    dismiss: () => {
      if (!operation || operation.status === 'running' || sending) return;
      generation.current += 1; clearTimeout(timer.current); reading.current = null;
      setOperation(null); setError(''); persist(null);
    } };
}
